import { apiRequest } from "./client";
import {
    EventCategoryItem,
    EventCategoryCreatePayload,
    EventCategoryUpdatePayload,
    PriceTypeItem,
    PriceTypeCreatePayload,
    PriceTypeUpdatePayload,
    HallCreatePayload,
    HallItem,
    HallListResponse,
    HallPhotoUploadResponse,
    HallFromPartnerSeanceResponse,
    NashePerformanceItem,
    TourItem,
    TourCreatePayload,
    TourUpdatePayload,
    OrganizerItem,
    OrganizerCreatePayload,
    OrganizerUpdatePayload,
    ToursListResponse,
    TourCityItem,
    TourCityCreatePayload,
    CountryItem,
    CityItem,
    PromoCodeItem,
    PromoCodeCreatePayload,
    PromoCodeUpdatePayload,
    DiscountItem,
    DiscountCreatePayload,
    DiscountUpdatePayload,
} from "../types/event-content.types";


interface GetHallsParams {
    page: number;
    size: number;
    search?: string;
    cityId?: string | null;
}

export const getHallsRequest = async (
    params: GetHallsParams
): Promise<HallListResponse> => {
    const query = new URLSearchParams();

    query.set("page", String(params.page));
    query.set("page_size", String(params.size));

    if (params.search?.trim()) {
        query.set("search_name", params.search.trim());
    }

    if (params.cityId) {
        query.set("city_id", params.cityId);
    }

    return apiRequest<HallListResponse>(`/event-content/halls?${query.toString()}`, {
        method: "GET",
        auth: true,
    });
};
export const getHallsByCityRequest = async (
    params: GetHallsParams
): Promise<HallListResponse> => {
    const query = new URLSearchParams();

    query.set("page", String(params.page));
    query.set("page_size", String(params.size));

    if (params.search?.trim()) {
        query.set("search_city", params.search.trim());
    }

    return apiRequest<HallListResponse>(`/event-content/halls?${query.toString()}`, {
        method: "GET",
        auth: true,
    });
};
export const createHallRequest = async (
    payload: { address_ru: string | null; address_de: string | null; website: string | null; is_active: boolean; description_en: string | null; sort: number; description_ru: string | null; photos: string[]; zip_code: string | null; description_de: string | null; name_ru: string; name_de: string; address_en: string | null; country_id: string | null; name_en: string; city_id: string | null }
): Promise<HallItem> => {
    return apiRequest<HallItem>("/event-content/halls", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const updateHallRequest = async (
    hallId: string,
    payload: { photos: string[] }
): Promise<HallItem> => {
    return apiRequest<HallItem>(`/event-content/halls/${hallId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const createHallFromPartnerSeanceRequest = async (
    seanceId: number
): Promise<HallFromPartnerSeanceResponse> => {
    return apiRequest<HallFromPartnerSeanceResponse>("/event-content/halls/from-partner-seance", {
        method: "POST",
        body: JSON.stringify({ seance_id: seanceId }),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

// Импортирует данные сеанса Artist Production НАПРЯМУЮ в уже существующий зал
// (полностью заменяя его текущую схему секциями/местами партнёра).
export const importPartnerSeanceIntoHallRequest = async (
    hallId: string,
    seanceId: number
): Promise<HallFromPartnerSeanceResponse> => {
    return apiRequest<HallFromPartnerSeanceResponse>(
        `/event-content/halls/${hallId}/import-partner-seance`,
        {
            method: "POST",
            body: JSON.stringify({ seance_id: seanceId }),
            headers: {
                "Content-Type": "application/json",
            },
            auth: true,
        }
    );
};

// ---- Партнёр Nashe (nashe.de) ----

// Список мероприятий, доступных нашему ключу Nashe.
export const listNashePerformancesRequest = async (): Promise<{ performances: NashePerformanceItem[] }> => {
    return apiRequest<{ performances: NashePerformanceItem[] }>("/event-content/nashe/performances", {
        method: "GET",
        auth: true,
    });
};

// Создаёт новый зал по схеме зала мероприятия Nashe.
export const createHallFromNashePerformanceRequest = async (
    performanceId: number
): Promise<HallFromPartnerSeanceResponse> => {
    return apiRequest<HallFromPartnerSeanceResponse>("/event-content/halls/from-nashe-performance", {
        method: "POST",
        body: JSON.stringify({ performance_id: performanceId }),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

// Импортирует схему зала мероприятия Nashe в существующий зал (текущая схема заменяется).
export const importNashePerformanceIntoHallRequest = async (
    hallId: string,
    performanceId: number
): Promise<HallFromPartnerSeanceResponse> => {
    return apiRequest<HallFromPartnerSeanceResponse>(
        `/event-content/halls/${hallId}/import-nashe-performance`,
        {
            method: "POST",
            body: JSON.stringify({ performance_id: performanceId }),
            headers: {
                "Content-Type": "application/json",
            },
            auth: true,
        }
    );
};

export const uploadHallPhotoRequest = async (
    hallId: string,
    file: File
): Promise<HallPhotoUploadResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    return apiRequest<HallPhotoUploadResponse>(`/event-content/halls/upload-photo/${hallId}`, {
        method: "POST",
        body: formData,
        auth: true,
    });
};

export const deleteHallPhotoRequest = async (
    hallId: string,
    photoUrl: string
): Promise<void> => {
    await apiRequest<void>(`/event-content/halls/${hallId}/photos`, {
        method: "DELETE",
        body: JSON.stringify({
            photo_url: photoUrl,
        }),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

// Categories
export const getEventCategoriesRequest = async (): Promise<EventCategoryItem[]> => {
    return apiRequest<EventCategoryItem[]>("/event-content/categories", {
        method: "GET",
        auth: true,
    });
};

export const createEventCategoryRequest = async (
    payload: EventCategoryCreatePayload,
): Promise<EventCategoryItem> => {
    return apiRequest<EventCategoryItem>("/event-content/categories", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const updateEventCategoryRequest = async (
    categoryId: string,
    payload: EventCategoryUpdatePayload,
): Promise<EventCategoryItem> => {
    return apiRequest<EventCategoryItem>(`/event-content/categories/${categoryId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const deleteEventCategoryRequest = async (categoryId: string): Promise<void> => {
    await apiRequest<void>(`/event-content/categories/${categoryId}`, {
        method: "DELETE",
        auth: true,
    });
};

// Price types
export const getPriceTypesRequest = async (): Promise<PriceTypeItem[]> => {
    return apiRequest<PriceTypeItem[]>("/event-content/price-types", {
        method: "GET",
        auth: true,
    });
};

export const createPriceTypeRequest = async (
    payload: PriceTypeCreatePayload,
): Promise<PriceTypeItem> => {
    return apiRequest<PriceTypeItem>("/event-content/price-types", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const updatePriceTypeRequest = async (
    typeId: string,
    payload: PriceTypeUpdatePayload,
): Promise<PriceTypeItem> => {
    return apiRequest<PriceTypeItem>(`/event-content/price-types/${typeId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const deletePriceTypeRequest = async (typeId: string): Promise<void> => {
    await apiRequest<void>(`/event-content/price-types/${typeId}`, {
        method: "DELETE",
        auth: true,
    });
};





interface GetToursParams {
    page: number;
    size: number;
    search?: string;
}

export const getCategoriesRequest = async (): Promise<EventCategoryItem[]> => {
    return apiRequest<EventCategoryItem[]>("/event-content/categories", {
        method: "GET",
        auth: true,
    });
};


export const getToursRequest = async (
    params: GetToursParams
): Promise<ToursListResponse | TourItem[]> => {
    const query = new URLSearchParams();

    query.set("page", String(params.page));
    query.set("page_size", String(params.size));

    if (params.search?.trim()) {
        query.set("search_name", params.search.trim());
    }

    return apiRequest<ToursListResponse | TourItem[]>(`/event-content/tours?${query.toString()}`, {
        method: "GET",
        auth: true,
    });
};

export const createTourRequest = async (payload: { end_at: string; full_desc_ru: string | null; blocked_button_text_en: string | null; is_published: boolean; api_sales_allowed: boolean; start_at: string; photos: string[]; is_sale_blocked: boolean; category_id: string | null; name_ru: string; short_desc_en: string | null; name_de: string; slug: string | null; organizer_id: string | null; blocked_button_text_ru: string | null; blocked_button_text_de: string | null; cover_image: string | null; is_active: boolean; full_desc_en: string | null; sort: number | null; short_desc_ru: string | null; short_desc_de: string | null; full_desc_de: string | null; name_en: string; utm_source: string | null; youtube_iframe: string | null }): Promise<TourItem> => {
    return apiRequest<TourItem>("/event-content/tours", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const updateTourRequest = async (
    tourId: string,
    payload: TourUpdatePayload
): Promise<TourItem> => {
    return apiRequest<TourItem>(`/event-content/tours/${tourId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};


export const uploadTourCoverRequest = async (
    tourId: string,
    file: File,
): Promise<TourItem> => {
    const formData = new FormData();
    formData.append("file", file);

    return apiRequest<TourItem>(`/event-content/tours/upload-cover/${tourId}`, {
        method: "POST",
        body: formData,
        auth: true,
    });
};

export const uploadTourPhotoRequest = async (
    tourId: string,
    file: File,
): Promise<TourItem> => {
    const formData = new FormData();
    formData.append("file", file);

    return apiRequest<TourItem>(`/event-content/tours/upload-photo/${tourId}`, {
        method: "POST",
        body: formData,
        auth: true,
    });
};

export const deleteTourPhotoRequest = async (
    tourId: string,
    photoUrl: string,
): Promise<TourItem> => {
    return apiRequest<TourItem>(`/event-content/tours/${tourId}/photos`, {
        method: "DELETE",
        body: JSON.stringify({ photo_url: photoUrl }),
        headers: { "Content-Type": "application/json" },
        auth: true,
    });
};


export const getOrganizersRequest = async (): Promise<OrganizerItem[]> => {
    return apiRequest<OrganizerItem[]>("/event-content/organizers", {
        method: "GET",
        auth: true,
    });
};

export const createOrganizerRequest = async (
    payload: OrganizerCreatePayload,
): Promise<OrganizerItem> => {
    return apiRequest<OrganizerItem>("/event-content/organizers", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        auth: true,
    });
};

export const updateOrganizerRequest = async (
    organizerId: string,
    payload: OrganizerUpdatePayload,
): Promise<OrganizerItem> => {
    return apiRequest<OrganizerItem>(`/event-content/organizers/${organizerId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        auth: true,
    });
};


export const getTourCitiesRequest = async (tourId: string): Promise<TourCityItem[]> =>
    apiRequest<TourCityItem[]>(`/event-content/tours-cities?tour_id=${tourId}`, {
        method: "GET",
        auth: true,
    });

export const getTourCityRequest = async (tourCityId: string): Promise<TourCityItem> =>
    apiRequest<TourCityItem>(`/event-content/tours-cities/${tourCityId}`, {
        method: "GET",
        auth: true,
    });

export const createTourCityRequest = async (
    payload: TourCityCreatePayload,
): Promise<TourCityItem> =>
    apiRequest<TourCityItem>("/event-content/tours-cities", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        auth: true,
    });

export const updateTourCityRequest = async (
    tourCityId: string,
    payload: TourCityUpdatePayload & { is_deleted?: boolean },
): Promise<TourCityItem> =>
    apiRequest<TourCityItem>(`/event-content/tours-cities/${tourCityId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        auth: true,
    });

export const getCountriesRequest = async (): Promise<CountryItem[]> =>
    apiRequest<CountryItem[]>("/geo/countries", {
        method: "GET",
        auth: true,
    });

export const getCitiesRequest = async (countryId?: string): Promise<CityItem[]> => {
    const query = new URLSearchParams();

    if (countryId) {
        query.set("country_id", countryId);
    }

    return apiRequest<CityItem[]>(`/geo/cities${query.toString() ? `?${query.toString()}` : ""}`, {
        method: "GET",
        auth: true,
    });
};
export const deleteTourCityRequest = async (tourCityId: string): Promise<void> => {
    await apiRequest<void>(`/event-content/tours-cities/${tourCityId}`, {
        method: "DELETE",
        auth: true,
    });
};
export const getCountryRequest = async (countryId: string): Promise<CountryItem> =>
    apiRequest<CountryItem>(`/geo/countries/${countryId}`, {
        method: "GET",
        auth: true,
    });

export const getCityRequest = async (cityId: string): Promise<CityItem> =>
    apiRequest<CityItem>(`/geo/cities/${cityId}`, {
        method: "GET",
        auth: true,
    });

export const getHallRequest = async (hallId: string): Promise<HallItem> =>
    apiRequest<HallItem>(`/event-content/halls/${hallId}`, {
        method: "GET",
        auth: true,
    });



export const getPromoCodesRequest = async (): Promise<PromoCodeItem[]> => {
    return apiRequest<PromoCodeItem[]>("/event-content/promo-codes", {
        method: "GET",
        auth: true,
    });
};

export const createPromoCodeRequest = async (
    payload: { starts_at: string | null; code: string; is_active: boolean; tour_ids: string[]; discount_value: number; ends_at: string | null; sort: number; discount_type: "percent" | "amount" },
): Promise<PromoCodeItem> => {
    return apiRequest<PromoCodeItem>("/event-content/promo-codes", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const updatePromoCodeRequest = async (
    promoCodeId: string,
    payload: PromoCodeUpdatePayload,
): Promise<PromoCodeItem> => {
    return apiRequest<PromoCodeItem>(`/event-content/promo-codes/${promoCodeId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};


// ---------- Скидки ----------

export const getDiscountsRequest = async (): Promise<DiscountItem[]> => {
    return apiRequest<DiscountItem[]>("/event-content/discounts", {
        method: "GET",
        auth: true,
    });
};

export const createDiscountRequest = async (
    payload: DiscountCreatePayload,
): Promise<DiscountItem> => {
    return apiRequest<DiscountItem>("/event-content/discounts", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const updateDiscountRequest = async (
    discountId: string,
    payload: DiscountUpdatePayload,
): Promise<DiscountItem> => {
    return apiRequest<DiscountItem>(`/event-content/discounts/${discountId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
        auth: true,
    });
};

export const deleteDiscountRequest = async (discountId: string): Promise<void> => {
    await apiRequest<void>(`/event-content/discounts/${discountId}`, {
        method: "DELETE",
        auth: true,
    });
};
